import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sidebar, type SidebarTheme } from "./Sidebar";
import { Header } from "./Header";
import { MobileTabBar } from "./MobileTabBar";
import { MobileMenuSheet } from "./MobileMenuSheet";
import { SpaceBanner } from "./SpaceBanner";
import { MyAccountModal } from "@/components/account/MyAccountModal";
import { IdleWatcher } from "@/components/account/IdleWarning";
import { A2hsPrompts } from "@/components/a2hs/A2hsPrompts";
import { CctvPipHost } from "@/pages/Dispatch/CctvPipHost";
import { openMyAccount } from "@/hooks/useMyAccountModal";
import { useRowHighlight } from "@/hooks/useRowHighlight";
import { useViewportScrollReset } from "@/hooks/useViewportScrollReset";

/**
 * 앱 전체 골격 — Sidebar + Header + <Outlet /> + 모달들.
 * 라우팅은 router/routes.tsx 에서 정의됨.
 */
/** 사이드바 고정핀 기능 스위치 — false 로 두면 핀 버튼이 사라지고 옛 동작(항상 push, 접힘 미저장)으로 원복.
 *  며칠 테스트 후 "왼쪽바 원복" 결정용. 원복 시 이 상수만 false. */
const SIDEBAR_PIN_FEATURE = true;
const LS_PINNED = "rmc.sidebar.pinned";
const LS_COLLAPSED = "rmc.sidebar.collapsed";

function readBool(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === "1";
  } catch {
    return fallback;
  }
}
function writeBool(key: string, v: boolean) {
  try { localStorage.setItem(key, v ? "1" : "0"); } catch { /* private mode 등 */ }
}

export function Layout() {
  // 고정핀(기기별 저장). ON=push 방식(옛 동작), OFF=64px 레일 + hover/햄버거로 오버레이(fold 와 동일 CSS)
  const [pinned, setPinned] = useState(() => (SIDEBAR_PIN_FEATURE ? readBool(LS_PINNED, true) : true));
  const [collapsed, setCollapsed] = useState(() => (SIDEBAR_PIN_FEATURE ? readBool(LS_COLLAPSED, false) : false));
  // fold 드로어·PC 핀 OFF 오버레이가 같은 상태를 공유 (CSS 클래스 is-mobile-nav-open)
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const contentRef = useRef<HTMLElement>(null);
  const blurRef = useRef<HTMLDivElement>(null);

  // 표 행 클릭 강조(전역 위임) — hooks/useRowHighlight.ts
  useRowHighlight();

  useEffect(() => { if (SIDEBAR_PIN_FEATURE) writeBool(LS_PINNED, pinned); }, [pinned]);
  useEffect(() => { if (SIDEBAR_PIN_FEATURE) writeBool(LS_COLLAPSED, collapsed); }, [collapsed]);

  // 핀 OFF hover 열기/닫기 — 스쳐 지나갈 때 깜빡이지 않게 열기 300ms · 닫기 200ms 지연
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearHoverTimer = () => {
    if (hoverTimer.current) { clearTimeout(hoverTimer.current); hoverTimer.current = null; }
  };
  const isPcUnpinned = () => !pinned && window.matchMedia("(min-width: 1024px)").matches;
  const onSidebarHoverIn = () => {
    if (!isPcUnpinned()) return;
    clearHoverTimer();
    hoverTimer.current = setTimeout(() => setMobileNavOpen(true), 300);
  };
  const onSidebarHoverOut = () => {
    if (!isPcUnpinned()) return;
    clearHoverTimer();
    hoverTimer.current = setTimeout(() => setMobileNavOpen(false), 200);
  };
  useEffect(() => clearHoverTimer, []);

  // 핀 토글은 "접기/펴기"가 아니라 배치 방식 전환(fixed 오버레이 ↔ grid push)이다.
  // 이때 사이드바 컬럼은 64px↔240px 로 값이 튀는데, 전환이 걸려 있으면 이미 펼쳐져 보이던
  // 사이드바가 한 번 접혔다가 다시 펴진다 → 한 프레임만 --sb-anim 을 0 으로 눌러 둔다.
  const [sbAnimOff, setSbAnimOff] = useState(false);
  const suppressSbAnim = () => {
    setSbAnimOff(true);
    // 다음 페인트까지 기다렸다가 복구 — 바로 풀면 같은 프레임이라 전환이 다시 걸린다
    requestAnimationFrame(() => requestAnimationFrame(() => setSbAnimOff(false)));
  };

  const togglePinned = () => {
    clearHoverTimer();
    suppressSbAnim();
    if (pinned) {
      // 고정 해제: 지금 보고 있는 펼침 상태를 오버레이로 유지하고, 마우스가 떠나면 레일로 접힘
      setPinned(false);
      setMobileNavOpen(true);
    } else {
      // 고정: 오버레이를 닫고 push 펼침으로 전환
      setPinned(true);
      setCollapsed(false);
      setMobileNavOpen(false);
    }
  };

  // iOS 키보드 닫힘 후 문서가 위로 밀린 채 남는 문제 보정 — hooks/useViewportScrollReset.ts
  useViewportScrollReset();

  // MO 상단 frosted 밴드(.scroll-edge-blur): 스크롤 양(0~RANGE px)에 비례해 0→1 페이드.
  // 맨 위에서는 안 보이다가 스크롤하면서 자연스럽게 차오름. 리렌더 없이 CSS 변수만 갱신.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const RANGE = 56;
    const apply = () => {
      const t = Math.min(Math.max(el.scrollTop, 0) / RANGE, 1);
      blurRef.current?.style.setProperty("--scroll-edge-shown", String(t));
    };
    apply();
    el.addEventListener("scroll", apply, { passive: true });
    return () => el.removeEventListener("scroll", apply);
  }, []);

  // 라우트가 바뀌면(메뉴/탭 이동) MO 바텀시트 메뉴 닫고, 콘텐츠를 맨 위로 스크롤
  const location = useLocation();
  useEffect(() => {
    setMobileMenuOpen(false);
    contentRef.current?.scrollTo({ top: 0, left: 0 });
    blurRef.current?.style.setProperty("--scroll-edge-shown", "0");
  }, [location.pathname]);

  const sidebarTheme: SidebarTheme = "dark";
  const headerStyle = "elevated";
  const density = "comfortable";

  return (
    <div
      className={`app hd-${headerStyle} ${mobileNavOpen ? "is-mobile-nav-open" : ""} ${!pinned ? "is-unpinned" : ""} ${sbAnimOff ? "is-sb-anim-off" : ""}`}
      data-density={density}
    >
      <Sidebar
        collapsed={pinned ? collapsed : false}
        onToggleCollapsed={() => setCollapsed((c) => !c)}
        theme={sidebarTheme}
        pinned={pinned}
        onTogglePinned={SIDEBAR_PIN_FEATURE ? togglePinned : undefined}
        onToggleMobile={() => { clearHoverTimer(); setMobileNavOpen((o) => !o); }}
        onItemClick={() => setMobileNavOpen(false)}
        onHoverIn={onSidebarHoverIn}
        onHoverOut={onSidebarHoverOut}
      />
      {mobileNavOpen && <div className="mobile-overlay" onClick={() => setMobileNavOpen(false)} />}
      <div className="main-col">
        <div className="header-wrap">
          <Header
            onOpenSettings={() => openMyAccount()}
            onOpenMobileNav={() => setMobileNavOpen(true)}
          />
        </div>
        <main className="content" ref={contentRef}>
          <SpaceBanner />
          <Outlet />
        </main>
        {/* MO 전용: 상태바(시계·배터리) 영역을 덮는 frosted 스크롤 엣지 밴드.
            스크롤되는 콘텐츠가 뒤를 지나가면 흐려져 보임. CSS로 MO에서만 표시 */}
        <div className="scroll-edge-blur" aria-hidden="true" ref={blurRef} />
        <MobileTabBar menuOpen={mobileMenuOpen} onToggleMenu={() => setMobileMenuOpen((o) => !o)} />
        <MobileMenuSheet open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
      </div>
      {/* 내 계정·환경(계정 메뉴·계정 시트에서 엶) + PC 유휴 경고 — 12_통합관리_계정기기설계 §8-D·§7-2 */}
      <MyAccountModal />
      <IdleWatcher />
      {/* 모바일: 홈 화면 추가 안내(브라우저) · 알림 켜기 안내(홈 화면 앱 첫 진입) */}
      <A2hsPrompts />
      {/* 공장 CCTV 라이브 창 — 최소화 창은 페이지를 옮겨도 유지(출하조회 썸네일에서 엶) */}
      <CctvPipHost />
    </div>
  );
}
